import { vDist, type BoardNode, type NodeId } from '@kanvas/board-core';
import { DURATION, duration, easeOutCubic, easeOutQuint, progressOf } from './motion';
import { drawWorldPoints } from '@kanvas/board-core';

/**
 * Per-node entrance and exit animations.
 *
 * These live entirely outside the document. An animation is a purely local,
 * transient fact — "this object appeared on *my* screen just now" — so putting
 * it in the CRDT would replicate it to peers who have already seen the object,
 * and would pollute undo history. The registry is keyed by node id and the
 * renderer consults it while painting.
 */

export type AnimKind =
  /** Scale-and-fade arrival, for objects that are simply there now. */
  | 'enter'
  /** Progressive reveal along the geometry, for strokes and connectors. */
  | 'draw'
  /** Fade-and-shrink departure; the node is already gone from the document. */
  | 'exit';

interface NodeAnim {
  kind: AnimKind;
  start: number;
  duration: number;
  /** Kept for exits, since the node no longer exists to be read back. */
  snapshot?: BoardNode;
}

export interface AnimState {
  kind: AnimKind;
  /** Eased 0..1. */
  t: number;
  /** Raw 0..1, for reveals that should travel linearly. */
  linear: number;
}

/**
 * A batch larger than this is a document load or a bulk paste, not something a
 * person just did. Animating it would produce a wave of motion across the whole
 * board for no informational gain.
 */
const MAX_ANIMATED_BATCH = 24;

/** Reveal speed for stroke draw-on, in board units per second. */
const REVEAL_SPEED = 1400;

class AnimationRegistry {
  private anims = new Map<NodeId, NodeAnim>();

  /**
   * Objects arriving. `remote` decides the treatment: something a peer drew
   * gets replayed as a stroke so you can see *what they did*, while something
   * you drew yourself was already visible under your pointer and only needs a
   * confirmation pop.
   */
  enter(
    nodes: readonly BoardNode[],
    remote: boolean,
    /**
     * Board-space length of a node's geometry. Connectors resolve their route
     * through the document, which this module cannot reach, so the caller
     * supplies the measurement.
     */
    measureLength?: (node: BoardNode) => number,
  ): void {
    if (nodes.length === 0 || nodes.length > MAX_ANIMATED_BATCH) return;
    const now = performance.now();

    for (const node of nodes) {
      // Groups and hidden nodes are never painted, so `stateOf` would never
      // retire their entry and the renderer would repaint every frame forever.
      if (node.type === 'group' || node.hidden) continue;
      const revealable = node.type === 'draw' || node.type === 'connector';
      if (remote && revealable) {
        this.anims.set(node.id, {
          kind: 'draw',
          start: now,
          duration: duration(revealDuration(node, measureLength)),
        });
        continue;
      }
      // A local stroke needs nothing: the ink was already on screen.
      if (!remote && node.type === 'draw') continue;

      this.anims.set(node.id, {
        kind: 'enter',
        start: now,
        duration: duration(remote ? DURATION.normal : DURATION.instant),
      });
    }
  }

  exit(nodes: readonly BoardNode[]): void {
    if (nodes.length === 0 || nodes.length > MAX_ANIMATED_BATCH) return;
    const now = performance.now();
    for (const node of nodes) {
      this.anims.set(node.id, {
        kind: 'exit',
        start: now,
        duration: duration(DURATION.fast),
        snapshot: node,
      });
    }
  }

  /** State for a node being painted, or null when it is fully settled. */
  stateOf(id: NodeId, now: number): AnimState | null {
    const anim = this.anims.get(id);
    if (!anim || anim.kind === 'exit') return null;

    const linear = progressOf(anim.start, anim.duration, now);
    if (linear >= 1) {
      this.anims.delete(id);
      return null;
    }
    return {
      kind: anim.kind,
      linear,
      t: anim.kind === 'draw' ? easeOutQuint(linear) : easeOutCubic(linear),
    };
  }

  /** Nodes that have been deleted but are still fading out. */
  ghosts(now: number): { node: BoardNode; t: number }[] {
    const out: { node: BoardNode; t: number }[] = [];
    for (const [id, anim] of this.anims) {
      if (anim.kind !== 'exit' || !anim.snapshot) continue;
      const linear = progressOf(anim.start, anim.duration, now);
      if (linear >= 1) {
        this.anims.delete(id);
        continue;
      }
      out.push({ node: anim.snapshot, t: easeOutCubic(linear) });
    }
    return out;
  }

  /**
   * Whether another frame is needed. Expired entries are pruned here too:
   * `stateOf` only sees nodes that get painted, so one that is culled
   * off-screen would otherwise keep the render loop spinning.
   */
  active(now = performance.now()): boolean {
    for (const [id, anim] of this.anims) {
      if (progressOf(anim.start, anim.duration, now) >= 1) this.anims.delete(id);
    }
    return this.anims.size > 0;
  }

  /** Drops everything, e.g. when the board is swapped out. */
  clear(): void {
    this.anims.clear();
  }

  /**
   * Cancels the animation for a node. Used when a gesture starts on it: an
   * object being dragged must track the pointer exactly, not a stale tween.
   */
  cancel(ids: readonly NodeId[]): void {
    for (const id of ids) {
      const anim = this.anims.get(id);
      if (anim && anim.kind !== 'exit') this.anims.delete(id);
    }
  }
}

export const animations = new AnimationRegistry();

/**
 * How long a reveal should take.
 *
 * Tied to the geometry's own length so a flick and a long sweeping stroke both
 * read as being drawn at a believable speed, then clamped so neither extreme
 * becomes annoying.
 */
function revealDuration(
  node: BoardNode,
  measureLength?: (node: BoardNode) => number,
): number {
  let length = 0;

  if (node.type === 'draw') {
    const points = drawWorldPoints(node);
    for (let i = 1; i < points.length; i++) length += vDist(points[i - 1]!, points[i]!);
  } else {
    length = measureLength?.(node) ?? 240;
  }

  const ms = (length / REVEAL_SPEED) * 1000;
  return Math.min(Math.max(ms, 220), 900);
}
